import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import api from "@/api/client";

// ---------- Base English translations (master) ----------
const en = {
  "nav.all": "All Categories",
  "nav.about": "About Us",
  "nav.contact": "Contact",
  "nav.sell": "Sell on FarmersAgroHub",
  "nav.help": "Help",
  "nav.account": "Account",
  "nav.cart": "Cart",
  "nav.login": "Login",
  "nav.advisor": "KrishiBot AI",
  "nav.delivery": "Delivering across India · 18,000+ pincodes",
  "nav.delivery.short": "Pan-India delivery",
  "search.placeholder": "Search for seeds, pesticides, sprayers, machinery…",
  "search.button": "Search",
  "hero.tagline": "India's #1 Multi-Vendor Agri Marketplace",
  "hero.title.1": "Connecting Farmers,",
  "hero.title.2": "Empowering",
  "hero.title.3": "Agriculture",
  "hero.sub": "Genuine seeds, pesticides, fertilizers, irrigation & farm machinery from verified GST-registered sellers. Delivered to 18,000+ pincodes across India.",
  "hero.search.placeholder": "Search seeds, sprayers, machinery…",
  "hero.search.btn": "Find Products",
  "hero.usp.1": "100% Genuine Products",
  "hero.usp.2": "Pan-India Delivery",
  "hero.usp.3": "COD Available",
  "home.cat.eyebrow": "Shop by Category",
  "home.cat.title": "Everything your farm needs",
  "home.cat.viewall": "View all →",
  "home.featured.eyebrow": "Top Picks",
  "home.featured.title": "Featured Products",
  "home.featured.shopall": "Shop all →",
  "home.sellers.eyebrow": "Verified Network",
  "home.sellers.title": "Trusted Sellers",
  "home.sellers.sub": "Hand-picked, GST-verified agri businesses across India.",
  "home.sellers.cta": "Become a Seller",
  "home.reviews.eyebrow": "From the Farm",
  "home.reviews.title": "Farmers love FarmersAgroHub",
  "home.reviews.sub": "Real reviews from real farmers across Bharat.",
  "home.faq.eyebrow": "Need Help",
  "home.faq.title": "Frequently Asked Questions",
  "advisor.title": "KrishiBot — AI Crop Advisor",
  "advisor.sub": "Ask anything about crops, pests, fertilizers, irrigation & schemes.",
  "advisor.placeholder": "Ask in your language…",
  "advisor.send": "Send",
  "advisor.empty": "Hi! I'm KrishiBot 🌾 — your AI farming advisor.",
  "advisor.suggest.1": "Best fertilizer for wheat?",
  "advisor.suggest.2": "How to control whitefly in cotton?",
  "advisor.suggest.3": "Drip irrigation schedule for tomato?",
  "advisor.suggest.4": "How to apply for PM-KISAN?",
  "lang.label": "Language",
};

// ---------- Partial manual translations for top-visible nav/hero strings ----------
// Anything missing in the manual map is auto-translated via backend AI on the fly.
const hi = {
  "nav.all": "सभी श्रेणियाँ", "nav.about": "हमारे बारे में", "nav.contact": "संपर्क",
  "nav.sell": "FarmersAgroHub पर बेचें", "nav.help": "सहायता", "nav.account": "खाता",
  "nav.cart": "कार्ट", "nav.login": "लॉगिन", "nav.advisor": "कृषिबॉट AI",
  "nav.delivery": "पूरे भारत में डिलीवरी · 18,000+ पिनकोड", "nav.delivery.short": "पूरे भारत डिलीवरी",
  "search.placeholder": "बीज, कीटनाशक, स्प्रेयर, मशीनरी खोजें…", "search.button": "खोजें",
  "hero.tagline": "भारत का #1 मल्टी-वेंडर कृषि बाज़ार",
  "hero.title.1": "किसानों को जोड़ना,", "hero.title.2": "कृषि को", "hero.title.3": "सशक्त बनाना",
  "hero.sub": "GST-वेरीफाइड विक्रेताओं से असली बीज, कीटनाशक, उर्वरक, सिंचाई और कृषि मशीनरी। 18,000+ पिनकोड पर डिलीवरी।",
  "hero.search.placeholder": "बीज, स्प्रेयर खोजें…", "hero.search.btn": "उत्पाद ढूँढें",
  "hero.usp.1": "100% असली उत्पाद", "hero.usp.2": "पूरे भारत डिलीवरी", "hero.usp.3": "COD उपलब्ध",
  "advisor.title": "कृषिबॉट — AI फसल सलाहकार",
  "advisor.sub": "फसल, कीट, उर्वरक, सिंचाई और सरकारी योजनाओं के बारे में पूछें।",
  "advisor.placeholder": "अपनी भाषा में पूछें…", "advisor.send": "भेजें",
  "advisor.empty": "नमस्ते! मैं कृषिबॉट 🌾 हूँ — आपका AI कृषि सलाहकार।",
  "lang.label": "भाषा",
};

const translations = { en, hi };

const I18nContext = createContext(null);

// ---------- Helpers ----------
const isTranslatable = (s) => typeof s === "string" && /[a-zA-Z]{2,}/.test(s) && s.length <= 800;

const dynKey = (lang) => `fah_dyn_${lang}`;

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("fah_lang") || "en");
  const [dynamic, setDynamic] = useState({});
  const pendingRef = useRef(new Set());
  const flushTimerRef = useRef(null);
  const inflightRef = useRef(false);

  // Persist lang + html attr
  useEffect(() => {
    localStorage.setItem("fah_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  // Load cached dynamic translations when lang changes
  useEffect(() => {
    if (lang === "en") { setDynamic({}); pendingRef.current.clear(); return; }
    try {
      const cached = JSON.parse(localStorage.getItem(dynKey(lang)) || "{}");
      setDynamic(cached || {});
    } catch { setDynamic({}); }
    pendingRef.current.clear();
  }, [lang]);

  const flushPending = useCallback(async () => {
    if (lang === "en") { pendingRef.current.clear(); return; }
    if (inflightRef.current) return;
    const items = Array.from(pendingRef.current);
    if (items.length === 0) return;
    pendingRef.current.clear();
    inflightRef.current = true;
    // Batch into <=50 to keep requests small
    const batch = items.slice(0, 50);
    const strings = {};
    batch.forEach((s) => { strings[s] = s; });
    try {
      const { data } = await api.post("/i18n/translate", { target_lang: lang, strings });
      setDynamic((prev) => {
        const merged = { ...prev, ...data };
        try { localStorage.setItem(dynKey(lang), JSON.stringify(merged)); } catch {}
        return merged;
      });
    } catch {
      // On failure, ignore — strings will remain in English
    } finally {
      inflightRef.current = false;
      // If more items got queued meanwhile, schedule another flush
      if (pendingRef.current.size > 0) {
        if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
        flushTimerRef.current = setTimeout(flushPending, 250);
      }
    }
  }, [lang]);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(flushPending, 350);
  }, [flushPending]);

  const t = useCallback((textOrKey) => {
    if (textOrKey == null) return textOrKey;
    if (typeof textOrKey !== "string") return textOrKey;
    if (textOrKey === "") return textOrKey;

    // Resolve to canonical English text:
    //   - if it's a known key, fetch en[key]
    //   - else treat the text itself as English
    const isKnownKey = Object.prototype.hasOwnProperty.call(en, textOrKey);
    const englishText = isKnownKey ? en[textOrKey] : textOrKey;

    if (lang === "en") return englishText;

    // 1) static manual map (key-based)
    if (isKnownKey && translations[lang] && translations[lang][textOrKey]) {
      return translations[lang][textOrKey];
    }
    // 2) static manual map (text-based) – translation indexed by English text
    if (translations[lang] && translations[lang][englishText]) {
      return translations[lang][englishText];
    }
    // 3) dynamic AI cache (indexed by English text)
    if (dynamic[englishText]) return dynamic[englishText];

    // 4) Queue for backend translation
    if (isTranslatable(englishText)) {
      if (!pendingRef.current.has(englishText)) {
        pendingRef.current.add(englishText);
        scheduleFlush();
      }
    }
    return englishText;
  }, [lang, dynamic, scheduleFlush]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);

// 22 scheduled languages of India + English. Order matches Indian Constitution 8th Schedule (alphabetical).
export const LANGUAGES = [
  { code: "en", label: "English", short: "EN", native: "English" },
  { code: "as", label: "Assamese", short: "AS", native: "অসমীয়া" },
  { code: "bn", label: "Bengali", short: "BN", native: "বাংলা" },
  { code: "brx", label: "Bodo", short: "BR", native: "बड़ो" },
  { code: "doi", label: "Dogri", short: "DO", native: "डोगरी" },
  { code: "gu", label: "Gujarati", short: "GU", native: "ગુજરાતી" },
  { code: "hi", label: "Hindi", short: "HI", native: "हिन्दी" },
  { code: "kn", label: "Kannada", short: "KN", native: "ಕನ್ನಡ" },
  { code: "ks", label: "Kashmiri", short: "KS", native: "کٲشُر" },
  { code: "kok", label: "Konkani", short: "KO", native: "कोंकणी" },
  { code: "mai", label: "Maithili", short: "MA", native: "मैथिली" },
  { code: "ml", label: "Malayalam", short: "ML", native: "മലയാളം" },
  { code: "mni", label: "Manipuri", short: "MN", native: "ꯃꯩꯇꯩꯂꯣꯟ" },
  { code: "mr", label: "Marathi", short: "MR", native: "मराठी" },
  { code: "ne", label: "Nepali", short: "NE", native: "नेपाली" },
  { code: "or", label: "Odia", short: "OR", native: "ଓଡ଼ିଆ" },
  { code: "pa", label: "Punjabi", short: "PA", native: "ਪੰਜਾਬੀ" },
  { code: "sa", label: "Sanskrit", short: "SA", native: "संस्कृतम्" },
  { code: "sat", label: "Santali", short: "ST", native: "ᱥᱟᱱᱛᱟᱲᱤ" },
  { code: "sd", label: "Sindhi", short: "SD", native: "سنڌي" },
  { code: "ta", label: "Tamil", short: "TA", native: "தமிழ்" },
  { code: "te", label: "Telugu", short: "TE", native: "తెలుగు" },
  { code: "ur", label: "Urdu", short: "UR", native: "اردو" },
];
